import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, CheckCircle2, AlertCircle, MessageCircle, Images } from "lucide-react";
import { useLang } from "@/i18n";
import { waLink, fmtDate } from "@/api";
import { dayStatus, nightlyPrice, minNightsFor, BOOKED } from "@/staticData";
import { toast } from "sonner";
import Lightbox from "@/components/Lightbox";
import { GALLERIES } from "@/roomGalleries";

const MONTHS = {
  ro: ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  it: ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"],
  de: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
  he: ["ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"],
  pl: ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"],
};
const WDAYS = {
  ro: ["L", "Ma", "Mi", "J", "V", "S", "D"],
  en: ["M", "T", "W", "T", "F", "S", "S"],
  it: ["L", "M", "M", "G", "V", "S", "D"],
  de: ["M", "D", "M", "D", "F", "S", "S"],
  he: ["ב", "ג", "ד", "ה", "ו", "ש", "א"],
  pl: ["P", "W", "Ś", "C", "P", "S", "N"],
};
const byLang = (map, lang) => map[lang] || map.en;

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookingSection({ units, config, prefill, fixedUnit }) {
  const { t, lang } = useLang();
  const rooms = units.filter((u) => u.type === "room");
  const house = units.find((u) => u.type === "house");
  const ordered = useMemo(() => [...rooms, ...(house ? [house] : [])], [units]);

  const [unitId, setUnitId] = useState("");
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [days, setDays] = useState({});
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", adults: 2, children: 0, children_ages: "", pet: false, gdpr: false });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [photoUnit, setPhotoUnit] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(null);

  const openPhotos = (u) => {
    setPhotoUnit(u);
    setPhotoIdx(0);
  };
  const closePhotos = () => setPhotoIdx(null);
  const galleryImages = photoUnit ? (GALLERIES[photoUnit.slug] || []) : [];
  const prevPhoto = () => setPhotoIdx((i) => (i === null ? null : (i - 1 + galleryImages.length) % galleryImages.length));
  const nextPhoto = () => setPhotoIdx((i) => (i === null ? null : (i + 1) % galleryImages.length));

  useEffect(() => {
    if (fixedUnit) setUnitId(fixedUnit);
  }, [fixedUnit]);

  useEffect(() => {
    if (!ordered.length || fixedUnit) return;
    const target = prefill.unitType === "house" ? house : rooms[0];
    if (target) setUnitId(target.id);
  }, [prefill.unitType, ordered.length]);

  useEffect(() => {
    if (prefill.checkIn) setCheckIn(prefill.checkIn);
    if (prefill.checkOut) setCheckOut(prefill.checkOut);
    if (prefill.adults) setForm((f) => ({ ...f, adults: prefill.adults, children: prefill.children, pet: prefill.pet }));
  }, [prefill]);

  const unit = ordered.find((u) => u.id === unitId);
  const unitName = unit ? (unit[`name_${lang}`] || unit.name_en || unit.name_ro) : "";

  // Perioadele ocupate: citite live din /data/booked.json (editabil de Mihai din /admin),
  // cu fallback pe datele statice din pachet.
  const [booked, setBooked] = useState(BOOKED);
  useEffect(() => {
    fetch("/data/booked.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && typeof d === "object") setBooked({ ...BOOKED, ...d }); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!unitId || !unit) return;
    const y = month.getFullYear();
    const m = month.getMonth();
    const count = new Date(y, m + 1, 0).getDate();
    const todayIso = iso(new Date());
    const map = {};
    for (let d = 1; d <= count; d++) {
      const ds = iso(new Date(y, m, d));
      const status = dayStatus(unitId, ds, todayIso, booked);
      map[ds] = { date: ds, status, price: status === "available" ? nightlyPrice(unit, ds) : null };
    }
    setDays(map);
  }, [unitId, month, unit, booked]);

  useEffect(() => {
    setQuote(null);
    if (checkIn && checkOut && unitId && unit) {
      const nights = [];
      for (let d = new Date(checkIn + "T12:00:00"); d < new Date(checkOut + "T12:00:00"); d.setDate(d.getDate() + 1)) {
        nights.push(iso(d));
      }
      if (!nights.length) return;
      const prices = nights.map((ds) => nightlyPrice(unit, ds));
      const mn = minNightsFor(unit, checkIn, checkOut);
      const available = nights.every((ds) => ["available", "limited"].includes(days[ds]?.status || "available"));
      setQuote({
        nights: nights.length,
        total: Math.round(prices.reduce((a, b) => a + b, 0) * 100) / 100,
        available,
        min_nights: mn,
        min_nights_ok: nights.length >= mn,
      });
    }
  }, [checkIn, checkOut, unitId, days]);

  const calendarCells = useMemo(() => {
    const y = month.getFullYear();
    const m = month.getMonth();
    const first = new Date(y, m, 1);
    const offset = (first.getDay() + 6) % 7;
    const count = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= count; d++) cells.push(iso(new Date(y, m, d)));
    return cells;
  }, [month]);

  const pick = (ds) => {
    const info = days[ds];
    if (!info || !["available", "limited"].includes(info.status)) return;
    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(ds);
      setCheckOut("");
      return;
    }
    if (ds <= checkIn) {
      setCheckIn(ds);
      return;
    }
    const ci = new Date(checkIn);
    const co = new Date(ds);
    let ok = true;
    for (let d = new Date(ci); d < co; d.setDate(d.getDate() + 1)) {
      const st = days[iso(d)]?.status;
      if (st && !["available", "limited"].includes(st)) { ok = false; break; }
    }
    if (!ok) {
      toast.error(t("bk.unavailable"));
      return;
    }
    setCheckOut(ds);
  };

  const inRange = (ds) => checkIn && checkOut && ds >= checkIn && ds < checkOut;

  const submit = async (e) => {
    e.preventDefault();
    if (!quote?.available || !quote?.min_nights_ok) return;
    setSending(true);
    setSuccess({
      check_in: checkIn, check_out: checkOut, nights: quote.nights,
      adults: form.adults, children: form.children, children_ages: form.children_ages,
      pets: form.pet, total: quote.total, guest_name: form.name,
    });
    setDays((prev) => {
      const next = { ...prev };
      for (let d = new Date(checkIn + "T12:00:00"); d < new Date(checkOut + "T12:00:00"); d.setDate(d.getDate() + 1)) {
        const k = iso(d);
        if (next[k]) next[k] = { ...next[k], status: "limited" };
      }
      return next;
    });
    setSending(false);
  };

  const waMsg = success
    ? t("wa.msg")
        .replace("{type}", unitName)
        .replace("{ci}", fmtDate(success.check_in))
        .replace("{co}", fmtDate(success.check_out))
        .replace("{nights}", success.nights)
        .replace("{adults}", success.adults)
        .replace("{children}", success.children + (success.children_ages ? ` (${success.children_ages})` : ""))
        .replace("{pet}", success.pets ? t("wa.pet_yes") : t("wa.pet_no"))
        .replace("{total}", success.total)
        .replace("{name}", success.guest_name)
    : "";

  const statusStyle = (st) => {
    switch (st) {
      case "available": return "bg-cream-light hover:bg-sage/30 text-main cursor-pointer";
      case "limited": return "bg-sage/25 text-green-dark cursor-pointer";
      case "booked": return "bg-terracotta/15 text-terracotta/60 line-through cursor-not-allowed";
      case "blocked": return "bg-warm-grey/30 text-warm-grey cursor-not-allowed";
      default: return "text-warm-grey/60 cursor-not-allowed";
    }
  };

  const canSubmit = quote?.available && quote?.min_nights_ok && form.name && form.phone && form.gdpr && !sending;

  return (
    <section id="rezervare" className="py-24 md:py-32 bg-cream-light border-y border-warm-grey/30" data-testid="booking-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.p initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3">
          <span className="inline-block w-10 h-px bg-terracotta" />
          {t("bk.eyebrow")}
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-5 font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main">
          {t("bk.title")}
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-4 text-brown/80 font-light max-w-xl">{t("bk.sub")}</motion.p>

        {!fixedUnit && (
        <div className="mt-10 flex flex-wrap gap-2" data-testid="unit-selector">
          {ordered.map((u) => (
            <div key={u.id} className="flex items-center gap-1">
              <button
                onClick={() => { setUnitId(u.id); setCheckIn(""); setCheckOut(""); setSuccess(null); }}
                data-testid={`unit-pill-${u.id}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                  unitId === u.id ? "bg-main text-cream-light" : "bg-cream border border-warm-grey/60 text-brown hover:border-terracotta"
                }`}
              >
                {u[`name_${lang}`] || u.name_ro}
                <span className="ml-2 text-xs opacity-70">{u.base_price}€{t("bk.per_night")}</span>
              </button>
              <button
                onClick={() => openPhotos(u)}
                data-testid={`unit-photos-${u.id}`}
                aria-label={`${t("bk.view_photos")} — ${u[`name_${lang}`] || u.name_ro}`}
                title={t("bk.view_photos")}
                className="w-9 h-9 rounded-full bg-cream border border-warm-grey/60 text-walnut flex items-center justify-center hover:bg-terracotta hover:text-cream-light hover:border-terracotta transition-all duration-300"
              >
                <Images size={15} />
              </button>
            </div>
          ))}
        </div>
        )}

        <div className="mt-8 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="bg-cream border border-warm-grey/40 rounded-3xl p-6 md:p-8" data-testid="availability-calendar">
              <div className="flex items-center justify-between">
                <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                  disabled={month <= new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
                  data-testid="cal-prev" className="p-2 rounded-full hover:bg-warm-grey/30 disabled:opacity-30 transition-colors">
                  <ChevronLeft size={20} />
                </button>
                <p className="font-serif text-2xl text-main" data-testid="cal-month-label">
                  {byLang(MONTHS, lang)[month.getMonth()]} {month.getFullYear()}
                </p>
                <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                  data-testid="cal-next" className="p-2 rounded-full hover:bg-warm-grey/30 transition-colors">
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="mt-6 grid grid-cols-7 gap-1 text-center text-[11px] font-bold tracking-widest text-walnut uppercase">
                {byLang(WDAYS, lang).map((d, i) => <span key={i} className="py-2">{d}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarCells.map((ds, i) => {
                  if (!ds) return <span key={`e${i}`} />;
                  const info = days[ds];
                  const st = info?.status || "available";
                  const selected = ds === checkIn || ds === checkOut;
                  const between = inRange(ds);
                  return (
                    <button
                      key={ds}
                      onClick={() => pick(ds)}
                      disabled={!info || !["available", "limited"].includes(st)}
                      data-testid={`cal-day-${ds}`}
                      className={`relative aspect-square rounded-xl flex flex-col items-center justify-center text-sm transition-all duration-200 ${statusStyle(st)} ${
                        selected ? "!bg-terracotta !text-cream-light font-bold scale-105 shadow-lg shadow-terracotta/30" : between ? "!bg-terracotta/25 !text-main" : ""
                      }`}
                    >
                      <span>{+ds.slice(8)}</span>
                      {info?.price != null && !selected && (
                        <span className="text-[9px] opacity-60 leading-none">{info.price}€</span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-brown/70" data-testid="cal-legend">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-cream-light border border-warm-grey/60" /> {t("bk.legend_avail")}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-sage/40" /> {t("bk.legend_limited")}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-terracotta/30" /> {t("bk.legend_booked")}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-warm-grey/40" /> {t("bk.legend_blocked")}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            {success ? (
              <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                className="bg-green-dark text-cream-light rounded-3xl p-8 md:p-10" data-testid="booking-success">
                <CheckCircle2 size={40} className="text-sage" />
                <h3 className="mt-5 font-serif text-3xl">{t("bk.success_title")}</h3>
                <p className="mt-3 text-cream-light/80 font-light text-sm leading-relaxed">{t("bk.success_text")}</p>
                <div className="mt-6 bg-cream-light/10 rounded-2xl p-5 text-sm space-y-1.5">
                  <p><strong>{unitName}</strong></p>
                  <p>{fmtDate(success.check_in)} – {fmtDate(success.check_out)} · {success.nights} {t("bk.nights")}</p>
                  <p className="font-serif text-2xl pt-1">{success.total} EUR</p>
                </div>
                <a href={waLink(config?.whatsapp || "40721663181", waMsg)} target="_blank" rel="noreferrer"
                  data-testid="booking-success-wa"
                  className="mt-6 flex items-center justify-center gap-2 rounded-full bg-cream-light text-green-dark font-semibold px-6 py-3.5 hover:bg-cream transition-colors animate-pulse shadow-lg shadow-black/20">
                  <MessageCircle size={18} /> {t("bk.success_wa")}
                </a>
                <button onClick={() => { setSuccess(null); setCheckIn(""); setCheckOut(""); setForm({ name: "", phone: "", email: "", message: "", adults: 2, children: 0, children_ages: "", pet: false, gdpr: false }); }}
                  data-testid="booking-new-request"
                  className="mt-4 w-full text-sm text-cream-light/70 hover:text-cream-light transition-colors">
                  {t("bk.new_request")}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="bg-cream border border-warm-grey/40 rounded-3xl p-6 md:p-8" data-testid="booking-form">
                <div className="flex items-center justify-between">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-walnut">{unitName}</p>
                  {quote && (
                    <p className="font-serif text-2xl text-terracotta" data-testid="booking-total">{quote.total}€</p>
                  )}
                </div>

                <div className="mt-4 rounded-2xl bg-cream-light border border-warm-grey/40 px-5 py-4 text-sm" data-testid="booking-selection-summary">
                  {checkIn && checkOut ? (
                    <div className="flex items-center justify-between">
                      <span>{fmtDate(checkIn)} → {fmtDate(checkOut)} · <strong>{quote?.nights ?? "…"} {t("bk.nights")}</strong></span>
                      {quote && (
                        quote.available && quote.min_nights_ok
                          ? <span className="text-green-dark font-semibold flex items-center gap-1"><CheckCircle2 size={15} /> {t("bk.available_msg")}</span>
                          : <span className="text-terracotta font-semibold flex items-center gap-1"><AlertCircle size={15} /> {!quote.available ? t("bk.unavailable") : t("bk.min_nights").replace("{n}", quote.min_nights)}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-brown/60">{t("bk.pick_dates")}</span>
                  )}
                </div>

                <div className="mt-5 grid grid-cols-2 gap-4">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("bk.name")}
                    data-testid="booking-name" className="col-span-2 bg-cream-light border border-warm-grey/60 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50" />
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("bk.phone")}
                    data-testid="booking-phone" className="bg-cream-light border border-warm-grey/60 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50" />
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("bk.email")}
                    data-testid="booking-email" className="bg-cream-light border border-warm-grey/60 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50" />
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-walnut">{t("bk.adults")}</span>
                    <select value={form.adults} onChange={(e) => setForm({ ...form, adults: +e.target.value })} data-testid="booking-adults"
                      className="bg-cream-light border border-warm-grey/60 rounded-xl px-3 py-2.5 text-sm focus:outline-none">
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-walnut">{t("bk.children")}</span>
                    <select value={form.children} onChange={(e) => setForm({ ...form, children: +e.target.value })} data-testid="booking-children"
                      className="bg-cream-light border border-warm-grey/60 rounded-xl px-3 py-2.5 text-sm focus:outline-none">
                      {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  {form.children > 0 && (
                    <input value={form.children_ages} onChange={(e) => setForm({ ...form, children_ages: e.target.value })} placeholder={t("bk.children_ages")}
                      data-testid="booking-children-ages" className="col-span-2 bg-cream-light border border-warm-grey/60 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50" />
                  )}
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("bk.message")} rows={2}
                    data-testid="booking-message" className="col-span-2 bg-cream-light border border-warm-grey/60 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/50 resize-none" />
                  <label className="col-span-2 flex items-center gap-2 text-sm text-brown cursor-pointer">
                    <input type="checkbox" checked={form.pet} onChange={(e) => setForm({ ...form, pet: e.target.checked })} className="w-4 h-4 accent-[#C67A5C]" data-testid="booking-pet" />
                    {t("bk.pet")}
                  </label>
                  <label className="col-span-2 flex items-start gap-2 text-xs text-brown/70 cursor-pointer">
                    <input type="checkbox" checked={form.gdpr} onChange={(e) => setForm({ ...form, gdpr: e.target.checked })} className="w-4 h-4 mt-0.5 accent-[#C67A5C]" data-testid="booking-gdpr" />
                    {t("bk.gdpr")}
                  </label>
                </div>

                <div className="mt-6 flex items-start gap-3 rounded-2xl bg-sage/15 border border-sage/40 px-4 py-3.5" data-testid="booking-wa-notice">
                  <MessageCircle size={18} className="text-green-dark shrink-0 mt-0.5" />
                  <p className="text-xs md:text-sm text-green-dark font-medium leading-relaxed">{t("bk.wa_notice")}</p>
                </div>

                <button type="submit" disabled={!canSubmit} data-testid="booking-submit"
                  className="mt-4 w-full rounded-full bg-terracotta text-cream-light font-semibold px-6 py-4 hover:bg-terracotta/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2">
                  <MessageCircle size={18} /> {sending ? t("bk.sending") : t("bk.submit")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Lightbox images={galleryImages} index={photoIdx} onClose={closePhotos} onPrev={prevPhoto} onNext={nextPhoto} lang={lang} />
    </section>
  );
}
